import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, MessageSquare } from "lucide-react";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleFeedback = () => {
    Sentry.showReportDialog({
      title: "Virheraportti",
      subtitle: "Kerro mitä tapahtui ennen virhettä.",
      subtitle2: "",
      labelName: "Nimi",
      labelEmail: "Sähköposti",
      labelComments: "Mitä tapahtui?",
      labelSubmit: "Lähetä",
      labelClose: "Sulje",
      successMessage: "Kiitos palautteesta!",
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">Jotain meni pieleen</h1>
              <p className="text-muted-foreground">
                Sovelluksessa tapahtui odottamaton virhe. Yritä ladata sivu uudelleen.
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="text-left p-4 bg-muted rounded-lg overflow-auto max-h-40">
                <p className="text-sm font-mono text-destructive">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReload} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Lataa sivu uudelleen
              </Button>
              <Button variant="outline" onClick={this.handleReset}>
                Yritä uudelleen
              </Button>
              <Button variant="ghost" onClick={this.handleFeedback} className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Lähetä palautetta
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
